import React, { useEffect, useMemo, useState, useSyncExternalStore } from "react";
import ReactHTMLTableToExcel from "react-html-table-to-excel";
import { formatAmount, getAllMonths, getAllYears } from "../../../../services/appService";
import { useDispatch, useSelector } from "react-redux";
import { getMonthlyGrossProfitCandidates, loadMonthlyGrossProfitCandidates } from "../../../store/reports";
import GeneratePdf from "../../../../components/GeneratePdf";

const GrossProfitCandidates = () => {
    const years = getAllYears();
    const months = getAllMonths();

    const candidateEntity = [
        { id: 1, name: "C2C Basis" },
        { id: 2, name: "W2 Basis" },
        { id: 3, name: "1099 Basis" },
    ];

    const [pdfShowHide, setPdfShowHide] = useState(false);
    const [headers, setHeaders] = useState([]);
    const [rows, setRows] = useState([]);

    const [year, setYear] = useState("");
    const [month, setMonth] = useState("");
    const [selectedEntity, setSelectedEntity] = useState("");

    const monthlyGrossProfit = useSelector(getMonthlyGrossProfitCandidates);
    const dispatch = useDispatch();


    // -----------------------------
    // Load Data
    // -----------------------------
    const GenerateGrossProfitRpt = () => {
        if (!year || !month) return;
        dispatch(loadMonthlyGrossProfitCandidates(year, month));
    };


    // -----------------------------
    // Filter Data by Entity
    // -----------------------------
    const filteredReport = useMemo(() => {
        if (!selectedEntity) return monthlyGrossProfit;

        return monthlyGrossProfit.filter(
            (x) => x.candidateType === selectedEntity
        );
    }, [monthlyGrossProfit, selectedEntity]);


    // -----------------------------
    // Totals Calculation
    // -----------------------------
    const totals = useMemo(() => {
        return filteredReport.reduce(
            (acc, item) => {
                acc.invoice += parseFloat(item.agencyAmount) || 0;
                acc.received += parseFloat(item.amountReceived) || 0;
                acc.candidate += parseFloat(item.candidateAmount) || 0;
                acc.subcontractor += parseFloat(item.subonctractorAmount) || 0; // original typo preserved
                acc.recruiter += parseFloat(item.recruiterAmount) || 0;
                acc.gross += parseFloat(item.grossProfit) || 0;
                return acc;
            },
            {
                invoice: 0,
                received: 0,
                candidate: 0,
                subcontractor: 0,
                recruiter: 0,
                gross: 0,
            }
        );
    }, [filteredReport]);


    // -----------------------------
    // Totals table row
    // -----------------------------
    const getTotalRow = () => (
        <tr className="font-weight-bold bg-lighter">
            <td colSpan={5} className="text-right">Total</td>
            <td className="text-right">{formatAmount(totals.invoice)}</td>
            <td className="text-right">{formatAmount(totals.received)}</td>
            <td className="text-right">{formatAmount(totals.candidate)}</td>
            <td className="text-right">{formatAmount(totals.subcontractor)}</td>
            <td className="text-right">{formatAmount(totals.recruiter)}</td>
            <td className="text-right">{formatAmount(totals.gross)}</td>
        </tr>
    );


    // -----------------------------
    // Generate PDF
    // -----------------------------
    const generateGrossProfitPdf = () => {
        const headerList = [
            [
                "#",
                "Candidate",
                "Entity",
                "Subcontractor",
                "Recruiter",
                "Invoiced $",
                "Received $",
                "Payable Candidate $",
                "Payable Subcontractor $",
                "Payable Recruiter $",
                "Gross Profit $",
            ],
        ];

        const rowList = filteredReport.map((item) => [
            item.candidateId,
            `${item.candidateName} ${item.candidateLastName || ""}`.trim(),
            item.candidateType,
            item.subcontractor,
            item.recruiter,
            formatAmount(item.agencyAmount),
            formatAmount(item.amountReceived),
            formatAmount(item.candidateAmount),
            formatAmount(item.subonctractorAmount),
            formatAmount(item.recruiterAmount),
            formatAmount(item.grossProfit),
        ]);

        // add totals row
        rowList.push([
            "Total",
            "",
            "",
            "",
            "",
            formatAmount(totals.invoice),
            formatAmount(totals.received),
            formatAmount(totals.candidate),
            formatAmount(totals.subcontractor),
            formatAmount(totals.recruiter),
            formatAmount(totals.gross),
        ]);

        setHeaders(headerList);
        setRows(rowList);
        setPdfShowHide(true);
    };


    // -----------------------------
    // Reset PDF modal
    // -----------------------------
    const handlepdfShowHide = () => {
        setPdfShowHide(false);
        setHeaders([]);
        setRows([]);
    };


    return (
        <>
            <div className="card shadow-none border">
                <div className="card-header text-center p-3 bg-lighter">
                    <h4 className="mb-0">Monthly Gross Profit Reports - Per Candidate Basis</h4>
                </div>

                <div className="card-body p-3">
                    <div className="row">
                        {/* Dropdowns */}
                        <div className="col-md-2">
                            <select
                                className="form-control form-control-sm"
                                value={year}
                                onChange={(e) => setYear(e.target.value)}
                            >
                                <option value="">Year</option>
                                {years.map((y) => (
                                    <option key={y.name} value={y.name}>
                                        {y.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="col-md-2">
                            <select
                                className="form-control form-control-sm"
                                value={month}
                                onChange={(e) => setMonth(e.target.value)}
                            >
                                <option value="">Month</option>
                                {months.map((m) => (
                                    <option key={m.id} value={m.id}>
                                        {m.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="col-md-3">
                            <select
                                className="form-control form-control-sm"
                                value={selectedEntity}
                                onChange={(e) => setSelectedEntity(e.target.value)}
                            >
                                <option value="">All</option>
                                {candidateEntity.map((item) => (
                                    <option key={item.id} value={item.name}>
                                        {item.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="col-md-2">
                            <button
                                className="btn btn-sm btn-outline-default"
                                disabled={!year || !month}
                                onClick={GenerateGrossProfitRpt}
                            >
                                Generate
                            </button>
                        </div>

                        <div className="col-md-3 text-right">
                            <ReactHTMLTableToExcel
                                id="xls-btn"
                                className="btn btn-sm btn-outline-default"
                                table="gross_profit_table"
                                filename="Monthly_Gross_Profit"
                                sheet="Monthly_Gross_Profit"
                                buttonText="Export to Excel"
                            />

                            <button
                                className="btn btn-sm btn-outline-default"
                                disabled={filteredReport.length === 0}
                                onClick={generateGrossProfitPdf}
                            >
                                Generate PDF
                            </button>
                        </div>

                        {/* Table */}
                        <div className="col-md-12 mt-2">
                            <div className="table-responsive">
                                <table
                                    className="table table-hover table-striped mb-2 border"
                                    id="gross_profit_table"
                                >
                                    <thead className="thead-light">
                                        <tr>
                                            <th colSpan={5}></th>
                                            <th colSpan={2} className="bg-white text-center">Accounts Receivable</th>
                                            <th colSpan={3} className="bg-lighter text-center">Accounts Payable</th>
                                            <th></th>
                                        </tr>

                                        <tr>
                                            <th>#</th>
                                            <th>Candidate</th>
                                            <th>Entity</th>
                                            <th>Subcontractor</th>
                                            <th>Recruiter</th>

                                            <th className="bg-white">Amount Invoiced $</th>
                                            <th className="bg-white">Amount Received $</th>

                                            <th className="bg-lighter">Payable Candidate $</th>
                                            <th className="bg-lighter">Payable Subcontractor $</th>
                                            <th className="bg-lighter">Payable Recruiter $</th>

                                            <th>Gross Profit</th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {filteredReport.map((item) => (
                                            <tr key={item.candidateId}>
                                                <td>{item.candidateId}</td>
                                                <td>{item.candidateName} {item.candidateLastName}</td>
                                                <td>{item.candidateType}</td>
                                                <td>{item.subcontractor}</td>
                                                <td>{item.recruiter}</td>

                                                <td className="text-right">{formatAmount(item.agencyAmount)}</td>
                                                <td className="text-right">{formatAmount(item.amountReceived)}</td>
                                                <td className="text-right">{formatAmount(item.candidateAmount)}</td>
                                                <td className="text-right">{formatAmount(item.subonctractorAmount)}</td>
                                                <td className="text-right">{formatAmount(item.recruiterAmount)}</td>
                                                <td className="text-right">{formatAmount(item.grossProfit)}</td>
                                            </tr>
                                        ))}

                                        {getTotalRow()}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* PDF Modal */}
            <GeneratePdf
                handlepdfShowHide={handlepdfShowHide}
                pdfShowHide={pdfShowHide}
                selectedCol={headers}
                selectedColData={rows}
            />
        </>
    );
};


export default GrossProfitCandidates;